import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Collapse,
  Divider,
  FormControl,
  FormControlLabel,
  FormLabel,
  IconButton,
  Link,
  MenuItem,
  Paper,
  Radio,
  RadioGroup,
  Step,
  StepLabel,
  Stepper,
  TextField,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { format, addDays, isBefore, isToday, set, startOfDay, getDay } from 'date-fns';
import { useLocation, useParams, useNavigate } from 'react-router-dom';
import cwLogo from '../../assets/cw-red-logo.svg';
import TimeSlotPicker from '../../shared/components/TimeSlotPicker';
import { CWYLoader } from '../../shared/components/CWYLoader';
import { API_ENDPOINTS } from '../../config/api';
import { formatTimeWithZone, formatDateWithZone } from '../../shared/utils/timezone';

const SERVICE_ID = '68390d1eb608e96c269aa229';
const SERVICE_NAME = 'Consultation';
const DEFAULT_BASE_PRICE = 13; // Default fallback price
const DEFAULT_TAX_RATE = 0.13; // Default tax rate (13% HST for CAD, 18% GST for INR)
const DEFAULT_CURRENCY = 'CAD';
const COUNTRY = 'CA';
// Fallback INR to CAD conversion rate (used if API fails)
const FALLBACK_INR_TO_CAD_RATE = 0.0167; // 1 INR = 0.0167 CAD (approximately 60 INR = 1 CAD)

interface TimeSlot {
  start: Date;
  end: Date;
  available: boolean;
}

interface BookerDetails {
  name: string;
  email: string;
  phoneCountryCode: string;
  phone: string;
  message: string;
}

interface StoredBookingPayload {
  bookingReference?: string;
  serviceId: string;
  bookerName: string;
  bookerEmail: string;
  phoneCountryCode: string;
  bookerPhone: string;
  notes: string;
  preferredDate: string;
  preferredTimeSlot: string;
  appointmentType: 'call' | 'walk-in';
  slotStart: string;
  slotEnd: string;
  couponCode?: string; // Coupon code used for this booking
  appointmentForUserId?: string; // The appointment taker's user ID
  appointmentForEmail?: string; // The appointment taker's email
  // Legacy support
  clientName?: string;
  clientEmail?: string;
  clientPhone?: string;
}

interface BookingConfirmation {
  bookerName: string;
  bookerEmail: string;
  bookerPhone: string;
  notes: string;
  start: Date;
  end: Date;
  appointmentType: 'call' | 'walk-in';
}

type PaymentStatus = 'idle' | 'processing' | 'verifying' | 'success' | 'cancelled' | 'failed';
type WalkInArrivalMode = 'arriving_now' | 'coming_later';

interface OfficeAddress {
  name: string;
  fullAddress: string;
  addressLine1?: string;
  addressLine2?: string;
  city?: string;
  province?: string;
  postalCode?: string;
}

interface AppointmentTaker {
  email: string;
  userId: string;
  displayName: string;
  slug: string | null;
  allowSaturday: boolean;
  allowSunday: boolean;
  businessHours: {
    start: number;
    startMinute: number;
    end: number;
    endMinute: number;
  } | null;
  timeZone: string;
  fee?: number; // Consultation fee (e.g., 13 for CAD, 3000 for INR)
  currency?: string; // Currency code (e.g., 'CAD', 'INR', 'USD')
  officeAddress?: OfficeAddress | null;
  allowWalkIn?: boolean; // Allow walk-in appointments (default: true)
  allowCall?: boolean; // Allow call appointments (default: true)
}

// Buffer time in minutes for "Coming later today" option
const WALK_IN_BUFFER_MINUTES = 30;

const steps = ['Choose Time', 'Your Details', 'Payment', 'Confirmation'];

const getStorageKey = (ref: string) => `cwy-booking-${ref}`;
const getSuccessStorageKey = (ref: string) => `cwy-booking-success-${ref}`;

// Deduplicated phone country codes array
const PHONE_COUNTRY_CODES = Array.from(new Set([
  '+1', '+91', '+44', '+61', '+971', '+86', '+33', '+49', '+81', '+82', '+65', '+60', '+62', '+66', '+84', 
  '+880', '+92', '+94', '+95', '+20', '+27', '+234', '+254', '+52', '+55', '+54', '+56', '+57', '+51', '+58', 
  '+593', '+506', '+507', '+595', '+598', '+592', '+597', '+501', '+502', '+504', '+505', '+509', '+212', 
  '+213', '+216', '+218', '+220', '+221', '+222', '+223', '+224', '+225', '+226', '+227', '+228', '+229', 
  '+230', '+231', '+232', '+233', '+235', '+236', '+237', '+238', '+239', '+240', '+241', '+242', '+243', 
  '+244', '+245', '+246', '+248', '+249', '+250', '+251', '+252', '+253', '+255', '+256', '+257', 
  '+258', '+260', '+261', '+262', '+263', '+264', '+265', '+266', '+267', '+268', '+269', '+290', '+291', 
  '+297', '+298', '+299', '+350', '+351', '+352', '+353', '+354', '+355', '+356', '+357', '+358', '+359', 
  '+370', '+371', '+372', '+373', '+374', '+375', '+376', '+377', '+378', '+380', '+381', '+382', '+383', 
  '+385', '+386', '+387', '+389', '+420', '+421', '+423', '+500', '+503', '+508', '+590', '+591', '+594', 
  '+596', '+599', '+670', '+672', '+673', '+674', '+675', '+676', '+677', '+678', '+679', '+680', '+681', '+682', 
  '+683', '+685', '+686', '+687', '+688', '+689', '+690', '+691', '+692', '+850', '+852', '+853', '+855', 
  '+856', '+886', '+960', '+961', '+962', '+963', '+964', '+965', '+966', '+967', '+968', '+970', 
  '+972', '+973', '+974', '+975', '+976', '+977', '+992', '+993', '+994', '+995', '+996', '+998'
])).sort();

const buildFullPhoneNumber = (countryCode: string, digits: string) => {
  const sanitizedCode = countryCode.startsWith('+') ? countryCode : `+${countryCode}`;
  const sanitizedDigits = digits.replace(/\D/g, '').slice(0, 10);
  return sanitizedDigits ? `${sanitizedCode}${sanitizedDigits}` : sanitizedCode;
};

const getPhoneValidationError = (value: string) => {
  const digits = value.replace(/\D/g, '');
  if (!digits) {
    return 'Phone number is required';
  }
  if (digits.length !== 10) {
    return 'Phone number must be exactly 10 digits';
  }
  return null;
};

const generateReference = () => {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }

  return `ref-${Date.now()}-${Math.floor(Math.random() * 100000)}`;
};

const BookingPage: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  
  const [activeStep, setActiveStep] = useState(0);
  const [date, setDate] = useState<Date | null>(new Date());
  const [selectedSlot, setSelectedSlot] = useState<TimeSlot | null>(null);
  const [bookerDetails, setBookerDetails] = useState<BookerDetails>({
    name: '',
    email: '',
    phoneCountryCode: '+1',
    phone: '',
    message: '',
  });

  // Debug: Log bookerDetails changes
  useEffect(() => {
    console.log('[BOOKING] bookerDetails changed:', bookerDetails);
  }, [bookerDetails]);
  const [error, setError] = useState<string | null>(null);
  const [appointmentType, setAppointmentType] = useState<'call' | 'walk-in'>('walk-in');
  const [walkInArrivalMode, setWalkInArrivalMode] = useState<WalkInArrivalMode>('arriving_now');
  const [paymentStatus, setPaymentStatusState] = useState<PaymentStatus>('idle');
  const [paymentMessage, setPaymentMessage] = useState<string | null>(null);
  const [finalizingBooking, setFinalizingBooking] = useState(false);
  const [bookingConfirmation, setBookingConfirmation] = useState<BookingConfirmation | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isAutoSelectedDate, setIsAutoSelectedDate] = useState(false);
  const [appointmentTakerTimeZone, setAppointmentTakerTimeZone] = useState<string>('America/Toronto'); // Default to EST
  const [couponCode, setCouponCode] = useState<string>('');
  const [couponValidating, setCouponValidating] = useState(false);
  const [showCouponField, setShowCouponField] = useState(false);
  const [couponValid, setCouponValid] = useState<{
    valid: boolean;
    error?: string;
    discount?: {
      originalAmount: number;
      discountAmount: number;
      finalAmount: number;
    };
    isFree?: boolean;
    coupon?: {
      code: string;
      discountType: string;
      discountValue: number;
      description?: string;
    };
  } | null>(null);
  
  // Appointment taker state
  const [appointmentTaker, setAppointmentTaker] = useState<AppointmentTaker | null>(null);
  const [loadingTaker, setLoadingTaker] = useState(true);
  const [takerError, setTakerError] = useState<string | null>(null);
  
  // Exchange rate state
  const [inrToCadRate, setInrToCadRate] = useState<number>(FALLBACK_INR_TO_CAD_RATE);

  // Wrapper to prevent changing payment status from 'success' to anything else
  const setPaymentStatus = useCallback((status: PaymentStatus) => {
    setPaymentStatusState((prevStatus) => {
      // Once we're in success state, freeze it - never allow changing to error/failed
      if (prevStatus === 'success' && status !== 'success') {
        console.log('[Booking] Payment already successful, ignoring status change to:', status);
        return prevStatus;
      }
      return status;
    });
  }, []);

  const location = useLocation();
  const finalizeInProgressRef = useRef(false);
  const handledSessionRef = useRef<string | null>(null);
  const hasCheckedInitialDate = useRef(false);

  // Detect user location and set country code for India when step 2 becomes active
  useEffect(() => {
    console.log('[BOOKING] activeStep changed to:', activeStep);
    console.log('[BOOKING] Current phoneCountryCode:', bookerDetails.phoneCountryCode);
    
    // Only detect if we're on step 2 and country code is still +1
    if (activeStep === 1) {
      console.log('[BOOKING] Step 2 (Your Details) is active');
      if (bookerDetails.phoneCountryCode === '+1') {
        console.log('[BOOKING] Country code is still +1, detecting location...');
        const detectLocation = async () => {
          try {
            console.log('[BOOKING] Fetching location from ipapi.co...');
            const response = await fetch('https://ipapi.co/json/');
            console.log('[BOOKING] Response status:', response.status, response.ok);
            const data = await response.json();
            console.log('[BOOKING] Location API response:', JSON.stringify(data, null, 2));
            const countryCode = data.country_code;
            console.log('[BOOKING] Detected country code:', countryCode);

            if (countryCode === 'IN') {
              console.log('[BOOKING] ✅ India detected! Setting country code to +91');
              setBookerDetails(prev => {
                console.log('[BOOKING] Previous bookerDetails before update:', prev);
                const updated = {
                  ...prev,
                  phoneCountryCode: '+91'
                };
                console.log('[BOOKING] ✅ Updated bookerDetails with +91:', updated);
                return updated;
              });
            } else {
              console.log('[BOOKING] Not India (country:', countryCode, '), keeping +1');
            }
          } catch (error) {
            console.error('[BOOKING] ❌ Error detecting location:', error);
            console.error('[BOOKING] Error details:', error instanceof Error ? error.message : String(error));
            // Silently fail - defaults will remain
          }
        };
        detectLocation();
      } else {
        console.log('[BOOKING] Country code already set to:', bookerDetails.phoneCountryCode, '- skipping detection');
      }
    }
  }, [activeStep, bookerDetails.phoneCountryCode]);

  // Also detect on initial mount
  useEffect(() => {
    console.log('[BOOKING] Initial mount, detecting location...');
    const detectLocation = async () => {
      try {
        console.log('[BOOKING] Fetching location from ipapi.co on mount...');
        const response = await fetch('https://ipapi.co/json/');
        const data = await response.json();
        console.log('[BOOKING] Location API response on mount:', data);
        const countryCode = data.country_code;
        console.log('[BOOKING] Detected country code on mount:', countryCode);

        if (countryCode === 'IN') {
          console.log('[BOOKING] India detected on mount, setting country code to +91');
          setBookerDetails(prev => {
            console.log('[BOOKING] Previous bookerDetails on mount:', prev);
            const updated = {
              ...prev,
              phoneCountryCode: '+91'
            };
            console.log('[BOOKING] Updated bookerDetails on mount:', updated);
            return updated;
          });
        } else {
          console.log('[BOOKING] Not India on mount, setting to +1');
          setBookerDetails(prev => ({
            ...prev,
            phoneCountryCode: '+1'
          }));
        }
      } catch (error) {
        console.error('[BOOKING] Error detecting location on mount:', error);
        // Silently fail - defaults will remain
      }
    };
    detectLocation();
  }, []);

  // Fetch CAD to INR exchange rate dynamically
  useEffect(() => {
    const fetchExchangeRate = async () => {
      // Only fetch if currency is INR or might be INR
      if (appointmentTaker?.currency === 'INR' || !appointmentTaker) {
        try {
          // Using exchangerate-api.com (free, no API key required for basic usage)
          // Alternative: You can use fixer.io, currencyapi.net, or your own backend endpoint
          const response = await fetch('https://api.exchangerate-api.com/v4/latest/CAD');
          const data = await response.json();
          
          if (data.rates && data.rates.INR) {
            // Get CAD to INR rate, then convert to INR to CAD
            const cadToInrRate = data.rates.INR; // e.g., 70.72 means 1 CAD = 70.72 INR
            const inrToCad = 1 / cadToInrRate; // Convert to INR to CAD rate
            console.log('[Booking] Fetched exchange rate - 1 CAD =', cadToInrRate, 'INR');
            console.log('[Booking] Using INR to CAD rate:', inrToCad);
            setInrToCadRate(inrToCad);
          } else {
            console.warn('[Booking] Exchange rate API response missing INR rate, using fallback');
            setInrToCadRate(FALLBACK_INR_TO_CAD_RATE);
          }
        } catch (error) {
          console.error('[Booking] Error fetching exchange rate:', error);
          console.log('[Booking] Using fallback exchange rate:', FALLBACK_INR_TO_CAD_RATE);
          setInrToCadRate(FALLBACK_INR_TO_CAD_RATE);
        }
      }
    };
    
    fetchExchangeRate();
  }, [appointmentTaker?.currency]);

  // Fetch appointment taker by slug on mount
  useEffect(() => {
    const fetchAppointmentTaker = async () => {
      if (!slug) {
        // No slug provided, redirect to landing page
        navigate('/', { replace: true });
        return;
      }

      setLoadingTaker(true);
      setTakerError(null);

      try {
        const response = await fetch(API_ENDPOINTS.APPOINTMENT_TAKER_BY_SLUG(slug));
        
        if (!response.ok) {
          if (response.status === 404) {
            setTakerError('Consultant not found. Please check the booking link.');
          } else {
            setTakerError('Unable to load booking page. Please try again later.');
          }
          return;
        }

        const data = await response.json();
        
        console.log('[Booking] Appointment taker API response:', JSON.stringify(data, null, 2));
        
        if (data.success && data.appointmentTaker) {
          console.log('[Booking] Setting appointment taker - userId:', data.appointmentTaker.userId, 'email:', data.appointmentTaker.email, 'displayName:', data.appointmentTaker.displayName);
          console.log('[Booking] Office address:', JSON.stringify(data.appointmentTaker.officeAddress));
          setAppointmentTaker(data.appointmentTaker);
          if (data.appointmentTaker.timeZone) {
            setAppointmentTakerTimeZone(data.appointmentTaker.timeZone);
          }
          // Update appointment type based on what's available
          // Prefer walk-in if available, otherwise use call
          if (data.appointmentTaker.allowWalkIn !== false) {
            setAppointmentType('walk-in');
          } else if (data.appointmentTaker.allowCall !== false) {
            setAppointmentType('call');
          }
        } else {
          setTakerError('Consultant not found. Please check the booking link.');
        }
      } catch (err) {
        console.error('Error fetching appointment taker:', err);
        setTakerError('Unable to load booking page. Please try again later.');
      } finally {
        setLoadingTaker(false);
      }
    };

    fetchAppointmentTaker();
  }, [slug, navigate]);

  // Get fee and currency from appointment taker, with fallbacks
  const basePrice = useMemo(() => appointmentTaker?.fee ?? DEFAULT_BASE_PRICE, [appointmentTaker]);
  const currency = useMemo(() => appointmentTaker?.currency || DEFAULT_CURRENCY, [appointmentTaker]);
  const taxRate = useMemo(() => {
    // No tax for INR, HST 13% for CAD/USD
    return currency === 'INR' ? 0 : DEFAULT_TAX_RATE;
  }, [currency]);
  
  const taxAmount = useMemo(() => {
    // No tax for INR
    return currency === 'INR' ? 0 : Number((basePrice * taxRate).toFixed(2));
  }, [basePrice, taxRate, currency]);
  
  const baseTotalAmount = useMemo(() => {
    // For INR, total is just the base price (no tax)
    return currency === 'INR' ? basePrice : Number((basePrice + taxAmount).toFixed(2));
  }, [basePrice, taxAmount, currency]);
  
  // Apply coupon discount if valid
  const totalAmount = useMemo(() => {
    if (couponValid?.valid && couponValid.discount) {
      return Number(couponValid.discount.finalAmount.toFixed(2));
    }
    return baseTotalAmount;
  }, [baseTotalAmount, couponValid]);

  const discountAmount = useMemo(() => {
    if (couponValid?.valid && couponValid.discount) {
      return couponValid.discount.discountAmount;
    }
    return 0;
  }, [couponValid]);

  const isFreeConsultation = useMemo(() => {
    return couponValid?.isFree === true || totalAmount === 0;
  }, [couponValid, totalAmount]);

  // Business hours constants (10:30 AM - 4:00 PM EST)
  const OFFICE_START_MINUTES = 10 * 60 + 30; // 10:30 AM
  const OFFICE_END_MINUTES = 16 * 60; // 4:00 PM

  const isOutsideOfficeHours = (checkDate: Date) => {
    const hour = checkDate.getHours();
    const minute = checkDate.getMinutes();
    const timeInMinutes = hour * 60 + minute;
    return timeInMinutes < OFFICE_START_MINUTES || timeInMinutes >= OFFICE_END_MINUTES;
  };

  // Check if we can accept walk-in "Arriving now" (must be within office hours)
  const canAcceptWalkInNow = useMemo(() => {
    const now = new Date();
    return !isOutsideOfficeHours(now);
  }, []);

  const getNextAvailableDate = (startDate: Date) => {
    let nextDate = addDays(startDate, 1);
    // Skip Saturday/Sunday based on appointment taker's availability
    while (true) {
      const dayOfWeek = getDay(nextDate); // 0 = Sunday, 6 = Saturday
      const isSaturday = dayOfWeek === 6;
      const isSunday = dayOfWeek === 0;
      
      // If it's Saturday and not allowed, skip to Sunday
      if (isSaturday && !appointmentTaker?.allowSaturday) {
        nextDate = addDays(nextDate, 1);
        continue;
      }
      // If it's Sunday and not allowed, skip to Monday
      if (isSunday && !appointmentTaker?.allowSunday) {
        nextDate = addDays(nextDate, 1);
        continue;
      }
      // If it's a weekday or allowed weekend day, return it
      break;
    }
    return nextDate;
  };

  const shouldDisableDate = (candidate: Date) => {
    const today = startOfDay(new Date());
    const selectedDate = startOfDay(candidate);
    if (isBefore(selectedDate, today)) return true;
    
    // Disable Saturday/Sunday based on appointment taker's availability
    const dayOfWeek = getDay(candidate); // 0 = Sunday, 6 = Saturday
    if (dayOfWeek === 6 && !appointmentTaker?.allowSaturday) {
      return true; // Disable Saturday if not allowed
    }
    if (dayOfWeek === 0 && !appointmentTaker?.allowSunday) {
      return true; // Disable Sunday if not allowed
    }
    
    return false;
  };

  const canProceedFromStep = (stepIndex: number) => {
    if (stepIndex === 0) {
      // Must have date and slot selected
      if (!date || !selectedSlot) return false;
      
      // For walk-in "Arriving now" on today, must be within business hours
      if (appointmentType === 'walk-in' && isToday(date) && walkInArrivalMode === 'arriving_now') {
        if (!canAcceptWalkInNow) return false;
        
        // For walk-in "arriving now", ensure the selected slot hasn't already started
        // If current time is 3:15, cannot book 3:00-3:30pm slot
        const now = new Date();
        if (selectedSlot.start < now) {
          return false; // Slot has already started
        }
      }
      
      // For walk-in "coming later today" or regular walk-in, ensure slot hasn't started
      if (appointmentType === 'walk-in' && isToday(date) && walkInArrivalMode === 'coming_later') {
        const now = new Date();
        // Round down to current 30-minute slot boundary
        const minutes = now.getMinutes();
        const roundedMinutes = Math.floor(minutes / 30) * 30;
        const currentSlotStart = set(now, { 
          hours: now.getHours(), 
          minutes: roundedMinutes, 
          seconds: 0, 
          milliseconds: 0 
        });
        // Slot must start after the current slot ends (current slot start + 30 minutes)
        const currentSlotEnd = new Date(currentSlotStart.getTime() + 30 * 60 * 1000);
        if (selectedSlot.start < currentSlotEnd) {
          return false; // Cannot book current or past slots
        }
      }
      
      return true;
    }

    if (stepIndex === 1) {
      return (
        bookerDetails.name.trim().length > 1 &&
        /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(bookerDetails.email.trim()) &&
        getPhoneValidationError(bookerDetails.phone) === null
      );
    }

    if (stepIndex === 2) {
      return paymentStatus === 'success';
    }

    return true;
  };

  const handleNext = () => {
    if (!canProceedFromStep(activeStep)) {
      return;
    }
    setActiveStep((prev) => Math.min(prev + 1, steps.length - 1));
  };

  const handleBookerChange =
    (field: keyof BookerDetails) =>
    (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      setBookerDetails((prev) => {
        const newDetails = {
          ...prev,
          [field]: event.target.value,
        };
        // If email changed and we have a valid coupon, re-validate it
        if (field === 'email' && couponCode && couponCode.trim().length >= 3 && couponValid?.valid) {
          // Re-validate when email changes if coupon was already applied
          validateCoupon(couponCode);
        }
        return newDetails;
      });
    };

  const handlePhoneNumberChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const digitsOnly = event.target.value.replace(/\D/g, '').slice(0, 10);
    setBookerDetails((prev) => ({
      ...prev,
      phone: digitsOnly,
    }));
  };

  const handleCountryCodeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setBookerDetails((prev) => ({
      ...prev,
      phoneCountryCode: event.target.value,
    }));
  };

  const validateCoupon = useCallback(async (code: string) => {
    if (!code.trim()) {
      setCouponValid(null);
      return;
    }

    setCouponValidating(true);
    try {
      const response = await fetch(API_ENDPOINTS.COUPON_VALIDATE, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          couponCode: code.trim(),
          originalAmount: baseTotalAmount,
          userEmail: bookerDetails.email.trim() || undefined, // Pass user email if available
          currency: currency, // Pass currency for proper validation
        }),
      });

      const data = await response.json();

      if (data.success) {
        setCouponValid({
          valid: true,
          discount: data.discount,
          isFree: data.isFree,
          coupon: data.coupon,
        });
        setError(null);
        // Keep coupon field visible when valid coupon is applied
        setShowCouponField(true);
      } else {
        // Use the specific error message from backend, or fallback to generic message
        const errorMessage = data.error || 'Invalid code';
        setCouponValid({
          valid: false,
          error: errorMessage,
        });
      }
    } catch (error) {
      console.error('Error validating coupon:', error);
      setCouponValid({
        valid: false,
        error: 'Invalid code',
      });
    } finally {
      setCouponValidating(false);
    }
  }, [baseTotalAmount, bookerDetails.email]);

  const handleCouponCodeChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const code = event.target.value.toUpperCase().trim();
    setCouponCode(code);
    
    // Clear validation state when user is typing
    if (couponValid !== null && code.length === 0) {
      setCouponValid(null);
    }
  }, [couponValid]);

  const handleCouponSubmit = useCallback(() => {
    if (couponCode.trim().length >= 3) {
      validateCoupon(couponCode.trim());
    }
  }, [couponCode, validateCoupon]);

  const handleCouponKeyPress = useCallback((event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      handleCouponSubmit();
    }
  }, [handleCouponSubmit]);

  const pollPaymentStatus = useCallback(
    async (sessionId: string, maxAttempts = 6, delayMs = 2000) => {
      for (let attempt = 0; attempt < maxAttempts; attempt += 1) {
        try {
          const response = await fetch(API_ENDPOINTS.STRIPE_STATUS(sessionId));

          if (!response.ok) {
            throw new Error('Could not verify payment status');
          }

          const data = await response.json();
          if (data.success && data.transaction) {
            const status = data.transaction.status;
            if (status === 'confirmed' || status === 'verified') {
              return true;
            }

            if (status === 'failed' || status === 'cancelled') {
              return false;
            }
          }
        } catch (statusError) {
          console.error('Payment status check error:', statusError);
        }

        // eslint-disable-next-line no-await-in-loop
        await new Promise((resolve) => setTimeout(resolve, delayMs));
      }

      return false;
    },
    []
  );

  const finalizeBooking = useCallback(
    async (reference: string, sessionId: string) => {
      // Check if we're already in success state - don't process again
      const successStorageKey = getSuccessStorageKey(reference);
      try {
        const successData = localStorage.getItem(successStorageKey);
        if (successData) {
          const parsed = JSON.parse(successData);
          if (parsed.status === 'success') {
            console.log('[Booking] Payment already successful, skipping finalizeBooking');
            return;
          }
        }
      } catch (error) {
        // Continue if check fails
      }

      if (finalizeInProgressRef.current) {
        console.log(
          '[Booking] finalizeBooking already running. Ignore duplicate trigger.',
          { reference, sessionId }
        );
        return;
      }
      const storageKey = getStorageKey(reference);
      const storedPayload = localStorage.getItem(storageKey);

      if (!storedPayload) {
        setPaymentStatus('failed');
        setPaymentMessage('We could not find your booking details. Please contact support if you were charged.');
        setActiveStep(3);
        try {
          window.history.replaceState(null, '', window.location.pathname);
        } catch (historyError) {
          console.warn('Failed to clear booking query params after missing payload:', historyError);
        }
        return;
      }

      finalizeInProgressRef.current = true;
      setActiveStep(3);
      setPaymentMessage(null);
      setFinalizingBooking(true);
      setPaymentStatus('verifying');
      setPaymentMessage('Verifying payment and finalizing your booking...');

      try {
        console.log('[Booking] Starting finalizeBooking', { reference, sessionId });
        const parsedPayload: StoredBookingPayload = JSON.parse(storedPayload);
        const paymentConfirmed = await pollPaymentStatus(sessionId);

        if (!paymentConfirmed) {
          throw new Error('Payment verification timed out. Please contact support if the charge appears on your statement.');
        }

        const payloadBookerName = parsedPayload.bookerName || parsedPayload.clientName || '';
        const payloadBookerEmail = parsedPayload.bookerEmail || parsedPayload.clientEmail || '';
        const payloadBookerPhone = parsedPayload.bookerPhone || parsedPayload.clientPhone || '';

        const response = await fetch(API_ENDPOINTS.CREATE_BOOKING, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            serviceId: parsedPayload.serviceId,
            bookerName: payloadBookerName,
            bookerEmail: payloadBookerEmail,
            bookerPhone: payloadBookerPhone,
            bookingReference: parsedPayload.bookingReference || reference,
            phoneCountryCode: parsedPayload.phoneCountryCode || '+1',
            notes: parsedPayload.notes,
            preferredDate: parsedPayload.preferredDate,
            preferredTimeSlot: parsedPayload.preferredTimeSlot,
            appointmentType: parsedPayload.appointmentType,
            couponCode: parsedPayload.couponCode || undefined,
            appointmentForUserId: parsedPayload.appointmentForUserId,
            appointmentForEmail: parsedPayload.appointmentForEmail,
          }),
        });

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          throw new Error(errorData.message || 'Booking failed');
        }

        const bookingResult = await response.json();

        const confirmation: BookingConfirmation = {
          bookerName: payloadBookerName,
          bookerEmail: payloadBookerEmail,
          bookerPhone: payloadBookerPhone,
          notes: parsedPayload.notes,
          start: new Date(parsedPayload.slotStart),
          end: new Date(parsedPayload.slotEnd),
          appointmentType: parsedPayload.appointmentType,
        };
        
        setBookingConfirmation(confirmation);
        setPaymentStatus('success');
        setPaymentMessage('Payment successful. Your consultation has been booked.');
        setActiveStep(3);
        setError(null);

        // Store success state in localStorage to persist across refreshes
        const successStorageKey = getSuccessStorageKey(reference);
        try {
          localStorage.setItem(successStorageKey, JSON.stringify({
            status: 'success',
            confirmation,
            timestamp: Date.now(),
          }));
        } catch (storageError) {
          console.warn('Failed to store success state:', storageError);
        }

        console.log('[Booking] Booking completed:', bookingResult);
      } catch (bookingError) {
        console.error('Finalize booking error:', bookingError);
        // Only set error if we're not already in success state
        // This prevents overwriting success state if error occurs after success
        if (paymentStatus !== 'success') {
          setPaymentStatus('failed');
          setPaymentMessage('Payment could not be completed. Please contact +1 647-829-8643.');
          setActiveStep(3);
        }
      } finally {
        finalizeInProgressRef.current = false;
        localStorage.removeItem(storageKey);
        setFinalizingBooking(false);
        try {
          window.history.replaceState(null, '', window.location.pathname);
        } catch (historyError) {
          console.warn('Failed to clear booking query params:', historyError);
        }
      }
    },
    [pollPaymentStatus]
  );

  // Check for persisted success state on mount/refresh
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const ref = params.get('ref');
    
    // If we have a ref, check if there's a persisted success state
    if (ref) {
      const successStorageKey = getSuccessStorageKey(ref);
      try {
        const successData = localStorage.getItem(successStorageKey);
        if (successData) {
          const parsed = JSON.parse(successData);
          if (parsed.status === 'success' && parsed.confirmation) {
            // Restore success state - payment is already complete
            setBookingConfirmation(parsed.confirmation);
            setPaymentStatus('success');
            setPaymentMessage('Payment successful. Your consultation has been booked.');
            setActiveStep(3);
            setError(null);
            // Clear URL params to prevent re-processing
            try {
              window.history.replaceState(null, '', window.location.pathname);
            } catch (historyError) {
              console.warn('Failed to clear booking query params:', historyError);
            }
            return; // Don't process further - we're already in success state
          }
        }
      } catch (error) {
        console.warn('Failed to check success state:', error);
      }
    }
  }, [location.search]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const sessionId = params.get('session_id');
    const ref = params.get('ref');
    const cancelled = params.get('payment') === 'cancelled';

    // Don't process if we're already in success state
    if (paymentStatus === 'success') {
      return;
    }

    if (cancelled) {
      setPaymentStatus('cancelled');
      setPaymentMessage('Payment was cancelled. You can try again or choose a different time slot.');
      setActiveStep(2);
      handledSessionRef.current = null;
      try {
        window.history.replaceState(null, '', window.location.pathname);
      } catch (historyError) {
        console.warn('Failed to clear booking query params after cancellation:', historyError);
      }
      return;
    }

    if (sessionId && ref) {
      // Check if we already have success state for this ref
      const successStorageKey = getSuccessStorageKey(ref);
      try {
        const successData = localStorage.getItem(successStorageKey);
        if (successData) {
          const parsed = JSON.parse(successData);
          if (parsed.status === 'success') {
            // Already successful, don't process again
            return;
          }
        }
      } catch (error) {
        // Continue with processing if check fails
      }

      if (handledSessionRef.current === sessionId) {
        return;
      }

      handledSessionRef.current = sessionId;
      finalizeBooking(ref, sessionId);
    }
  }, [location.search, finalizeBooking, paymentStatus]);


  // Auto-select next available date if walk-in and outside office hours on initial load or when switching to walk-in
  useEffect(() => {
    if (appointmentType === 'walk-in' && date && isToday(date)) {
      const now = new Date();
      if (isOutsideOfficeHours(now) && !hasCheckedInitialDate.current) {
        const nextDate = getNextAvailableDate(date);
        setDate(nextDate);
        setIsAutoSelectedDate(true);
        setSelectedSlot(null);
        hasCheckedInitialDate.current = true;
        return;
      }
    }
    // Reset when switching appointment types
    if (appointmentType === 'call') {
      hasCheckedInitialDate.current = false;
    }
  }, [appointmentType]);

  // Auto-select slot for walk-in today when "arriving now" mode is selected AND within office hours
  useEffect(() => {
    if (date && appointmentType === 'walk-in' && isToday(date) && walkInArrivalMode === 'arriving_now') {
      // Only auto-select slot if within office hours and date wasn't just auto-selected
      if (!isAutoSelectedDate && canAcceptWalkInNow) {
        const now = new Date();
        const minutes = now.getMinutes();
        const halfHour = Math.floor(minutes / 30) * 30;
        const startTime = set(now, { hours: now.getHours(), minutes: halfHour, seconds: 0, milliseconds: 0 });
        const start = set(date, { hours: startTime.getHours(), minutes: startTime.getMinutes(), seconds: 0, milliseconds: 0 });
        const end = new Date(start.getTime() + 30 * 60 * 1000);
        setSelectedSlot({ start, end, available: true });
      }
    } else if (appointmentType === 'call' || (appointmentType === 'walk-in' && !isToday(date!))) {
      // Reset slot when switching to call or non-today walk-in
      setSelectedSlot(null);
    }
    // Note: Don't reset slot for walk-in + today + coming_later - TimeSlotPicker handles that
  }, [appointmentType, date, isAutoSelectedDate, walkInArrivalMode, canAcceptWalkInNow]);

  // Auto-select "Coming later today" when outside office hours and walk-in + today is selected
  useEffect(() => {
    if (appointmentType === 'walk-in' && date && isToday(date) && !canAcceptWalkInNow) {
      setWalkInArrivalMode('coming_later');
    }
  }, [appointmentType, date, canAcceptWalkInNow]);

  const handlePayment = async () => {
    if (!selectedSlot || !date || isSubmitting) {
      return;
    }

    try {
      setIsSubmitting(true);
      setError(null);

      const normalizedPhone = buildFullPhoneNumber(
        bookerDetails.phoneCountryCode,
        bookerDetails.phone
      );
      const rawDigits = bookerDetails.phone.replace(/\D/g, '');

      if (rawDigits.length !== 10) {
        throw new Error('Phone number must include exactly 10 digits.');
      }
      const bookingReference = generateReference();
      const slotStartIso = selectedSlot.start.toISOString();
      const slotEndIso = selectedSlot.end.toISOString();

      const bookingPayload: StoredBookingPayload = {
        bookingReference,
        serviceId: SERVICE_ID,
        bookerName: bookerDetails.name.trim(),
        bookerEmail: bookerDetails.email.trim(),
        phoneCountryCode: bookerDetails.phoneCountryCode,
        bookerPhone: normalizedPhone,
        notes: bookerDetails.message.trim(),
        preferredDate: slotStartIso,
        preferredTimeSlot: format(selectedSlot.start, 'HH:mm'),
        appointmentType,
        slotStart: slotStartIso,
        slotEnd: slotEndIso,
        couponCode: couponCode.trim() || undefined, // Store coupon code if provided
        appointmentForUserId: appointmentTaker?.userId,
        appointmentForEmail: appointmentTaker?.email,
        clientName: bookerDetails.name.trim(),
        clientEmail: bookerDetails.email.trim(),
        clientPhone: normalizedPhone,
      };

      // If consultation is free (due to coupon), skip Stripe payment
      if (isFreeConsultation && couponValid?.valid) {
        setPaymentStatus('processing');
        setPaymentMessage('Creating your free consultation booking...');
        
        // Apply coupon (increment usage count)
        if (couponCode) {
          try {
            await fetch(API_ENDPOINTS.COUPON_APPLY, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
              },
              body: JSON.stringify({
                couponCode: couponCode.trim(),
                userEmail: bookerDetails.email.trim(), // Pass user email to track usage
              }),
            });
          } catch (error) {
            console.error('Error applying coupon:', error);
            // Continue with booking even if coupon apply fails
          }
        }

        // Directly create booking without payment
        const freeBookingPayload = {
          serviceId: SERVICE_ID,
          bookerName: bookerDetails.name.trim(),
          bookerEmail: bookerDetails.email.trim(),
          bookerPhone: normalizedPhone,
          bookingReference,
          phoneCountryCode: bookerDetails.phoneCountryCode,
          notes: bookerDetails.message.trim(),
          preferredDate: slotStartIso,
          preferredTimeSlot: format(selectedSlot.start, 'HH:mm'),
          appointmentType,
          couponCode: couponCode.trim() || undefined,
          appointmentForUserId: appointmentTaker?.userId,
          appointmentForEmail: appointmentTaker?.email,
        };
        
        console.log('[Booking] Creating free consultation with payload:', JSON.stringify(freeBookingPayload, null, 2));
        console.log('[Booking] Current appointmentTaker state at submission time:', appointmentTaker?.userId, appointmentTaker?.email, appointmentTaker?.displayName);
        
        const response = await fetch(API_ENDPOINTS.CREATE_BOOKING, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(freeBookingPayload),
        });

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          throw new Error(errorData.message || 'Booking failed');
        }

        await response.json(); // Booking created successfully

        const confirmation: BookingConfirmation = {
          bookerName: bookerDetails.name.trim(),
          bookerEmail: bookerDetails.email.trim(),
          bookerPhone: normalizedPhone,
          notes: bookerDetails.message.trim(),
          start: selectedSlot.start,
          end: selectedSlot.end,
          appointmentType,
        };
        
        setBookingConfirmation(confirmation);
        setPaymentStatus('success');
        setPaymentMessage('Your free consultation has been booked successfully!');
        setActiveStep(3);
        setIsSubmitting(false);
        return;
      }

      // Payment flow with Stripe for all currencies (CAD, USD, INR, etc.)
      // Stripe handles currency conversion and fees automatically
      setPaymentStatus('processing');
      setPaymentMessage('Redirecting to secure checkout...');

      console.log('[Booking] Storing paid booking payload - appointmentForUserId:', bookingPayload.appointmentForUserId, 'appointmentForEmail:', bookingPayload.appointmentForEmail, 'current appointmentTaker state:', appointmentTaker?.userId, appointmentTaker?.email);
      localStorage.setItem(getStorageKey(bookingReference), JSON.stringify(bookingPayload));

      const successUrl = `${window.location.origin}/?ref=${encodeURIComponent(
        bookingReference
      )}&session_id={CHECKOUT_SESSION_ID}`;
      const cancelUrl = `${window.location.origin}/?ref=${encodeURIComponent(
        bookingReference
      )}&payment=cancelled`;

      // Convert INR to CAD for Stripe (Stripe only accepts CAD)
      let stripeAmount = totalAmount;
      let stripeCurrency = currency;
      
      if (currency === 'INR') {
        // Convert INR to CAD using dynamically fetched rate
        stripeAmount = Number((totalAmount * inrToCadRate).toFixed(2));
        stripeCurrency = 'CAD';
        console.log(`[Booking] Converting ${totalAmount} ${currency} to ${stripeAmount} ${stripeCurrency} (rate: ${inrToCadRate})`);
      }

      // If amount is 0 (free consultation), skip payment and create booking directly
      if (stripeAmount === 0 || totalAmount === 0) {
        console.log('[Booking] Amount is 0, skipping payment and creating free booking');
        setPaymentStatus('processing');
        setPaymentMessage('Creating your free consultation booking...');
        
        // Directly create booking without payment
        const freeBookingPayload = {
          serviceId: SERVICE_ID,
          bookerName: bookerDetails.name.trim(),
          bookerEmail: bookerDetails.email.trim(),
          bookerPhone: normalizedPhone,
          bookingReference,
          phoneCountryCode: bookerDetails.phoneCountryCode,
          notes: bookerDetails.message.trim(),
          preferredDate: slotStartIso,
          preferredTimeSlot: format(selectedSlot.start, 'HH:mm'),
          appointmentType,
          couponCode: couponCode.trim() || undefined,
          appointmentForUserId: appointmentTaker?.userId,
          appointmentForEmail: appointmentTaker?.email,
        };
        
        console.log('[Booking] Creating free consultation with payload:', JSON.stringify(freeBookingPayload, null, 2));
        console.log('[Booking] Current appointmentTaker state at submission time:', appointmentTaker?.userId, appointmentTaker?.email, appointmentTaker?.displayName);
        
        const response = await fetch(API_ENDPOINTS.CREATE_BOOKING, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(freeBookingPayload),
        });

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          throw new Error(errorData.message || 'Booking failed');
        }

        await response.json(); // Booking created successfully

        const confirmation: BookingConfirmation = {
          bookerName: bookerDetails.name.trim(),
          bookerEmail: bookerDetails.email.trim(),
          bookerPhone: normalizedPhone,
          notes: bookerDetails.message.trim(),
          start: selectedSlot.start,
          end: selectedSlot.end,
          appointmentType,
        };
        
        setBookingConfirmation(confirmation);
        setPaymentStatus('success');
        setPaymentMessage('Your free consultation has been booked successfully!');
        setActiveStep(3);
        setIsSubmitting(false);
        return;
      }

      const checkoutPayload = {
        name: bookerDetails.name.trim(),
        phone: normalizedPhone,
        email: bookerDetails.email.trim(),
        country: COUNTRY,
        amount: stripeAmount,
        currency: stripeCurrency, // Always CAD for Stripe
        message: bookerDetails.message.trim() || `${SERVICE_NAME} booking`,
        successUrl,
        cancelUrl,
        bookingReference,
        phoneCountryCode: bookerDetails.phoneCountryCode,
        appointmentType,
        couponCode: couponCode.trim() || undefined, // Pass coupon code if provided
        // Store original currency and amount for reference
        originalCurrency: currency,
        originalAmount: currency === 'INR' ? totalAmount : undefined,
      };

      console.log('[Booking] Creating Stripe checkout session with payload:', checkoutPayload);

      try {
        localStorage.setItem(
          `cwy-booking-last-checkout-payload`,
          JSON.stringify(checkoutPayload)
        );
      } catch (storageError) {
        console.warn('Unable to persist checkout payload for debugging:', storageError);
      }

      const response = await fetch(API_ENDPOINTS.STRIPE_CHECKOUT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(checkoutPayload),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || errorData.message || 'Unable to start payment');
      }

      const data = await response.json();

      console.log('[Booking] Stripe checkout session response:', data);

      try {
        localStorage.setItem(
          `cwy-booking-last-checkout-response`,
          JSON.stringify(data)
        );
      } catch (storageError) {
        console.warn('Unable to persist checkout response for debugging:', storageError);
      }

      if (!data.success || !data.sessionUrl) {
        throw new Error('Payment session could not be created. Please try again later.');
      }

      if (data.redirects) {
        console.log('[Booking] Resolved redirect URLs:', data.redirects);
      }

      if (import.meta.env.DEV) {
        const debugDump = JSON.stringify(
          {
            request: checkoutPayload,
            response: data,
          },
          null,
          2
        );

        try {
          sessionStorage.setItem('cwy-booking-last-checkout-dump', debugDump);
        } catch (sessionError) {
          console.warn('Unable to persist checkout dump in sessionStorage:', sessionError);
        }

        if (navigator.clipboard && navigator.clipboard.writeText) {
          navigator.clipboard.writeText(debugDump).catch((clipboardError) => {
            console.warn('Failed to copy checkout dump to clipboard:', clipboardError);
          });
        }
      }

      window.location.href = data.sessionUrl;
    } catch (paymentError) {
      console.error('Payment initiation error:', paymentError);
      setPaymentStatus('failed');
      setPaymentMessage(
        paymentError instanceof Error
          ? paymentError.message
          : 'We encountered an issue while starting your payment. Please try again.'
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  // Calculate minTime to hide past slots for today's date
  // For walk-in "coming later today": current time + buffer
  // For walk-in regular and call type: current time rounded down to next 30-minute slot boundary
  // Memoize to avoid creating new Date on every render
  const minTimeForToday = useMemo(() => {
    if (!date || !isToday(date)) {
      return undefined;
    }

    const now = new Date();
    
    // For walk-in "coming later today" - add buffer
    if (appointmentType === 'walk-in' && walkInArrivalMode === 'coming_later') {
      return new Date(now.getTime() + WALK_IN_BUFFER_MINUTES * 60 * 1000);
    }
    
    // For walk-in regular and call type - hide past and current slots
    // Example: 1:36 PM -> round down to 1:30 PM (current slot start) -> add 30 min = 2:00 PM
    // This hides all slots starting at or before 1:30 PM (since TimeSlotPicker filters start < minTime)
    // So only slots starting at 2:00 PM or later will be shown
    if (appointmentType === 'walk-in' || appointmentType === 'call') {
      const minutes = now.getMinutes();
      const roundedMinutes = Math.floor(minutes / 30) * 30;
      const roundedTime = set(now, { 
        hours: now.getHours(), 
        minutes: roundedMinutes, 
        seconds: 0, 
        milliseconds: 0 
      });
      // Add 30 minutes to get the next slot boundary
      // This ensures the current 30-minute slot and all past slots are hidden
      const nextSlotTime = new Date(roundedTime.getTime() + 30 * 60 * 1000);
      return nextSlotTime;
    }
    
    return undefined;
  }, [appointmentType, date, walkInArrivalMode]);

  const renderStepContent = (stepIndex: number) => {
    const phoneValidationError = getPhoneValidationError(bookerDetails.phone);
    const showPhoneError = Boolean(bookerDetails.phone.length) && Boolean(phoneValidationError);
    const fullPhoneNumber = buildFullPhoneNumber(
      bookerDetails.phoneCountryCode,
      bookerDetails.phone
    );

    switch (stepIndex) {
      case 0: {
        const showWalkInArrivalOptions = appointmentType === 'walk-in' && date && isToday(date);

        return (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            <Typography variant="h6">Step 1: Choose Date and Time</Typography>
            <FormControl component="fieldset" sx={{ mb: 1, mt: 3 }}>
              <FormLabel component="legend">Appointment Type</FormLabel>
              <RadioGroup
                row
                name="appointment-type"
                value={appointmentType}
                onChange={(event) => {
                  const value = event.target.value === 'walk-in' ? 'walk-in' : 'call';
                  setAppointmentType(value);
                  setSelectedSlot(null);
                  // Reset walk-in arrival mode when changing appointment type
                  setWalkInArrivalMode('arriving_now');
                }}
              >
                {appointmentTaker?.allowWalkIn !== false && (
                  <FormControlLabel value="walk-in" control={<Radio />} label="Walk-in" />
                )}
                {appointmentTaker?.allowCall !== false && (
                  <FormControlLabel value="call" control={<Radio />} label="Call" />
                )}
              </RadioGroup>
              {appointmentTaker?.allowWalkIn === false && appointmentTaker?.allowCall === false && (
                <Alert severity="warning" sx={{ mt: 1 }}>
                  No appointment types are available for this consultant. Please contact support.
                </Alert>
              )}
            </FormControl>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <DatePicker
                label="Select Date"
                value={date}
                onChange={(newDate) => {
                  setSelectedSlot(null);
                  setDate(newDate);
                  setIsAutoSelectedDate(false); // User manually selected a date
                  // Reset walk-in arrival mode when changing date
                  setWalkInArrivalMode('arriving_now');
                  // If user manually selects today, allow them to proceed (don't auto-select next date)
                  if (newDate && isToday(newDate)) {
                    hasCheckedInitialDate.current = true; // Prevent auto-selecting next date
                  }
                }}
                shouldDisableDate={shouldDisableDate}
              />
            </LocalizationProvider>

            {/* Walk-in Arrival Options - Only shown for walk-in + today */}
            {showWalkInArrivalOptions && (
              <FormControl component="fieldset">
                <FormLabel component="legend">When will you arrive?</FormLabel>
                <RadioGroup
                  name="walk-in-arrival-mode"
                  value={walkInArrivalMode}
                  onChange={(event) => {
                    const value = event.target.value as WalkInArrivalMode;
                    setWalkInArrivalMode(value);
                    setSelectedSlot(null);
                    // If switching to "arriving now", auto-select the slot (only if within office hours)
                    if (value === 'arriving_now' && canAcceptWalkInNow) {
                      const now = new Date();
                      const minutes = now.getMinutes();
                      const halfHour = Math.floor(minutes / 30) * 30;
                      const startTime = set(now, { hours: now.getHours(), minutes: halfHour, seconds: 0, milliseconds: 0 });
                      const start = set(date!, { hours: startTime.getHours(), minutes: startTime.getMinutes(), seconds: 0, milliseconds: 0 });
                      const end = new Date(start.getTime() + 30 * 60 * 1000);
                      setSelectedSlot({ start, end, available: true });
                    }
                  }}
                >
                  <FormControlLabel 
                    value="arriving_now" 
                    control={<Radio />} 
                    label={canAcceptWalkInNow ? "Arriving now" : "Arriving now (office closed)"}
                    disabled={!canAcceptWalkInNow}
                  />
                  <FormControlLabel 
                    value="coming_later" 
                    control={<Radio />} 
                    label="Coming later today" 
                  />
                </RadioGroup>
                {!canAcceptWalkInNow && walkInArrivalMode === 'arriving_now' && (
                  <Alert severity="warning" sx={{ mt: 1 }}>
                    Walk-in arrivals are only available during office hours (10:30 AM – 4:00 PM EST). Please select "Coming later today" to book a time slot.
                  </Alert>
                )}
              </FormControl>
            )}

            {/* Slot display logic */}
            {date && (
              appointmentType === 'call' ? (
                // Call appointment - show slot picker with past slots hidden for today
                <TimeSlotPicker
                  date={date}
                  serviceId={SERVICE_ID}
                  onSlotSelect={setSelectedSlot}
                  minTime={minTimeForToday}
                  appointmentTaker={appointmentTaker ? {
                    email: appointmentTaker.email,
                    userId: appointmentTaker.userId,
                    timeZone: appointmentTaker.timeZone
                  } : undefined}
                />
              ) : isToday(date) ? (
                // Walk-in today - behavior depends on arrival mode
                walkInArrivalMode === 'arriving_now' ? (
                  canAcceptWalkInNow ? (
                    <Box sx={{ p: 2, bgcolor: 'grey.100', borderRadius: 2 }}>
                      <Typography variant="body1" sx={{ fontWeight: 500 }}>
                        Your walk-in time: {selectedSlot && `${formatTimeWithZone(selectedSlot.start, 'h:mm a', appointmentTakerTimeZone)} – ${formatTimeWithZone(selectedSlot.end, 'h:mm a', appointmentTakerTimeZone)}`}
                      </Typography>
                    </Box>
                  ) : null // Warning is shown above in the radio group section
                ) : (
                  // Coming later today - show filtered slot picker with buffer
                  <TimeSlotPicker
                    date={date}
                    serviceId={SERVICE_ID}
                    onSlotSelect={setSelectedSlot}
                    minTime={minTimeForToday}
                    emptyMessage="No walk-in slots left for today. Please choose another date."
                    appointmentTaker={appointmentTaker ? {
                      email: appointmentTaker.email,
                      userId: appointmentTaker.userId,
                      timeZone: appointmentTaker.timeZone
                    } : undefined}
                  />
                )
              ) : (
                // Walk-in on future date - show slot picker without time filtering
                <TimeSlotPicker
                  date={date}
                  serviceId={SERVICE_ID}
                  onSlotSelect={setSelectedSlot}
                  appointmentTaker={appointmentTaker ? {
                    email: appointmentTaker.email,
                    userId: appointmentTaker.userId,
                    timeZone: appointmentTaker.timeZone
                  } : undefined}
                />
              )
            )}
          </Box>
        );
      }
      case 1:
        return (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Typography variant="h6">Step 2: Your Details</Typography>
            <TextField
              label="Full Name"
              value={bookerDetails.name}
              onChange={handleBookerChange('name')}
              required
              fullWidth
            />
            <TextField
              label="Email"
              value={bookerDetails.email}
              onChange={handleBookerChange('email')}
              required
              fullWidth
              type="email"
              helperText="A booking confirmation and receipt will be sent to this email."
            />
            <Box sx={{ display: 'flex', gap: 1 }}>
              <TextField
                select
                label="Country Code"
                value={bookerDetails.phoneCountryCode}
                onChange={handleCountryCodeChange}
                sx={{ minWidth: 100 }}
              >
                {PHONE_COUNTRY_CODES.map((code) => (
                  <MenuItem key={code} value={code}>
                    {code}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                label="Phone Number"
                value={bookerDetails.phone}
                onChange={handlePhoneNumberChange}
                required
                fullWidth
                inputProps={{ inputMode: 'numeric', pattern: '[0-9]*', maxLength: 10 }}
                error={showPhoneError}
                helperText={showPhoneError ? phoneValidationError : ''}
              />
            </Box>
            <TextField
              label="Questions or Notes"
              value={bookerDetails.message}
              onChange={handleBookerChange('message')}
              multiline
              rows={3}
              fullWidth
              placeholder="Let us know anything specific you’d like to discuss."
            />
          </Box>
        );
      case 2:
        return (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            <Typography variant="h6">Step 3: Payment</Typography>
            <Paper variant="outlined" sx={{ p: 2, bgcolor: 'background.default' }}>
              <Typography variant="subtitle1" sx={{ mb: 1 }}>
                Appointment Summary
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {SERVICE_NAME}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Type: {appointmentType === 'call' ? 'Call' : 'Walk-in'}
              </Typography>
              {selectedSlot && (
                <>
                  <Typography variant="body2" color="text.secondary">
                    Slot: {formatTimeWithZone(selectedSlot.start, 'h:mm a', appointmentTakerTimeZone)} - {formatTimeWithZone(selectedSlot.end, 'h:mm a', appointmentTakerTimeZone)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Date: {formatDateWithZone(selectedSlot.start, 'EEEE, MMMM d', appointmentTakerTimeZone)}
                  </Typography>
                </>
              )}
              {/* Show office address for walk-in appointments */}
              {appointmentType === 'walk-in' && appointmentTaker?.officeAddress && (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  Address: {appointmentTaker.officeAddress.fullAddress}
                </Typography>
              )}

              <Divider sx={{ my: 2 }} />

              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Your Information
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Name: {bookerDetails.name}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Email: {bookerDetails.email}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Phone No: {bookerDetails.phone ? fullPhoneNumber : 'Not provided'}
              </Typography>
            </Paper>

            <Paper 
              variant="outlined" 
              sx={{ 
                p: 2, 
                bgcolor: 'background.default',
                borderColor: couponValid?.valid ? 'success.main' : 'divider',
                borderWidth: couponValid?.valid ? 1.5 : 1,
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  cursor: showCouponField || Boolean(couponCode) ? 'default' : 'pointer',
                  py: showCouponField || Boolean(couponCode) ? 0 : 0.5,
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    '& .promo-text': {
                      color: showCouponField || Boolean(couponCode) ? 'text.secondary' : 'primary.main',
                    },
                  },
                }}
                onClick={() => !showCouponField && !couponCode && setShowCouponField(true)}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <LocalOfferIcon 
                    sx={{ 
                      fontSize: 18, 
                      color: couponValid?.valid ? 'success.main' : 'text.secondary',
                      transition: 'color 0.2s ease',
                    }} 
                  />
                  <Typography
                    variant="body2"
                    className="promo-text"
                    sx={{
                      color: couponValid?.valid ? 'success.main' : 'text.secondary',
                      fontSize: '0.875rem',
                      fontWeight: showCouponField || Boolean(couponCode) ? 500 : 400,
                      transition: 'color 0.2s ease',
                    }}
                  >
                    {couponValid?.valid 
                      ? `Promo code: ${couponCode}` 
                      : showCouponField || Boolean(couponCode)
                        ? 'Promo code'
                        : 'Have a promo code?'
                    }
                  </Typography>
                </Box>
                {(showCouponField || Boolean(couponCode)) && !couponValid?.valid && (
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      setShowCouponField(false);
                      setCouponCode('');
                      setCouponValid(null);
                    }}
                    sx={{ p: 0.5 }}
                  >
                    <ExpandLessIcon fontSize="small" />
                  </IconButton>
                )}
                {!showCouponField && !couponCode && (
                  <IconButton size="small" sx={{ p: 0.5 }}>
                    <ExpandMoreIcon fontSize="small" />
                  </IconButton>
                )}
              </Box>

              <Collapse in={showCouponField || Boolean(couponCode)}>
                <Box sx={{ mt: 2 }}>
                  <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start', mb: 1.5 }}>
                    <TextField
                      label="Enter promo code"
                      value={couponCode}
                      onChange={handleCouponCodeChange}
                      onKeyPress={handleCouponKeyPress}
                      fullWidth
                      size="small"
                      disabled={couponValidating || paymentStatus === 'processing' || paymentStatus === 'success'}
                      helperText={
                        couponValidating
                          ? 'Validating...'
                          : couponValid?.valid
                            ? couponValid.coupon?.description || `Applied: ${couponValid.coupon?.code}`
                            : couponValid?.error || 'Enter your promo code and press Apply'
                      }
                      error={couponValid !== null && !couponValid.valid && couponCode.length > 0}
                      InputProps={{
                        sx: {
                          fontSize: '0.875rem',
                        },
                      }}
                      sx={{ flex: 1 }}
                    />
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={handleCouponSubmit}
                      disabled={couponValidating || !couponCode.trim() || paymentStatus === 'processing' || paymentStatus === 'success'}
                      sx={{
                        minWidth: 80,
                        textTransform: 'none',
                        fontSize: '0.875rem',
                        whiteSpace: 'nowrap',
                        mt: 0.5, // Align with input field (accounting for label)
                        height: '40px', // Match TextField height
                      }}
                    >
                      {couponValidating ? <CircularProgress size={16} /> : 'Apply'}
                    </Button>
                  </Box>
                  {couponValid?.valid && couponValid.discount && discountAmount > 0 && !isFreeConsultation && (
                    <Alert severity="success" sx={{ mb: 1.5, py: 0.5 }}>
                      <Typography variant="body2">
                        Discount applied: <strong>{currency} {discountAmount.toFixed(2)} off</strong>
                      </Typography>
                    </Alert>
                  )}
                  {isFreeConsultation && (
                    <Alert severity="success" sx={{ mb: 1.5, py: 0.5 }}>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        🎉 Your consultation is FREE with this promo code!
                      </Typography>
                    </Alert>
                  )}
                </Box>
              </Collapse>
            </Paper>

            <Paper variant="outlined" sx={{ p: 2, bgcolor: 'background.default' }}>
              <Typography variant="subtitle1" sx={{ mb: 1 }}>
                Price Details
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2">
                    Admin fee for consultation
                  </Typography>
                  <Typography variant="body2">
                    {currency} {basePrice.toFixed(2)}
                  </Typography>
                </Box>
                {currency !== 'INR' && taxAmount > 0 && (
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="body2">
                      HST ({Math.round(taxRate * 100)}%)
                    </Typography>
                    <Typography variant="body2">
                      {currency} {taxAmount.toFixed(2)}
                    </Typography>
                  </Box>
                )}
                {discountAmount > 0 && (
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="body2" color="success.main">
                      Discount ({couponValid?.coupon?.code})
                    </Typography>
                    <Typography variant="body2" color="success.main">
                      -{currency} {discountAmount.toFixed(2)}
                    </Typography>
                  </Box>
                )}
                <Divider sx={{ my: 0.5 }} />
                <Box sx={{ display: 'flex', justifyContent: 'space-between', fontWeight: 600 }}>
                  <Typography variant="body1" sx={{ fontWeight: 600 }}>
                    Total
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 600 }}>
                    {currency} {totalAmount.toFixed(2)}
                  </Typography>
                </Box>
              </Box>
            </Paper>

            {paymentMessage && (
              <Alert
                severity={
                  paymentStatus === 'failed'
                    ? 'error'
                    : paymentStatus === 'success'
                      ? 'success'
                      : paymentStatus === 'cancelled'
                        ? 'info'
                        : 'info'
                }
              >
                {paymentMessage}
              </Alert>
            )}

            <Button
              variant="contained"
              onClick={handlePayment}
              disabled={isSubmitting || paymentStatus === 'processing'}
              startIcon={isSubmitting ? <CircularProgress size={18} /> : undefined}
            >
              {isSubmitting
                ? isFreeConsultation
                  ? 'Creating booking...'
                  : 'Redirecting...'
                : isFreeConsultation
                  ? 'Book Free Consultation'
                  : 'Pay Now'}
            </Button>
          </Box>
        );
      case 3:
        return (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, textAlign: 'center' }}>
            {finalizingBooking ? (
              <>
                <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
                  <CWYLoader size={60} />
                </Box>
                <Alert severity="info">
                  <Typography>
                    Finalizing your booking… this may take a moment. Please do not refresh the page.
                  </Typography>
                </Alert>
              </>
            ) : (
              <Typography variant="h5">
                {paymentStatus === 'failed' ? 'Payment Error' : 'Appointment Confirmed'}
              </Typography>
            )}
            {!finalizingBooking && paymentStatus === 'success' && (
              <Alert severity="success">
                Your appointment has been booked. A confirmation email will be sent shortly.
              </Alert>
            )}
            {!finalizingBooking && paymentStatus === 'failed' && (
              <Alert severity="error">
                Payment couldn't be completed. Please contact +1 647-829-8643 for assistance.
              </Alert>
            )}
            {bookingConfirmation && !finalizingBooking && paymentStatus === 'success' && (
              <Paper variant="outlined" sx={{ p: 2, textAlign: 'left', bgcolor: 'background.default' }}>
                <Typography variant="subtitle1" sx={{ mb: 1 }}>
                  Appointment Summary
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Type: {bookingConfirmation.appointmentType === 'call' ? 'Call' : 'Walk-in'}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Slot: {formatTimeWithZone(bookingConfirmation.start, 'h:mm a', appointmentTakerTimeZone)} - {formatTimeWithZone(bookingConfirmation.end, 'h:mm a', appointmentTakerTimeZone)}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Date: {formatDateWithZone(bookingConfirmation.start, 'EEEE, MMMM d', appointmentTakerTimeZone)}
                </Typography>
                {/* Show office address for walk-in appointments */}
                {bookingConfirmation.appointmentType === 'walk-in' && appointmentTaker?.officeAddress && (
                  <Typography variant="body2" color="text.secondary">
                    Address: {appointmentTaker.officeAddress.fullAddress}
                  </Typography>
                )}
                {/* Payment info */}
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  {isFreeConsultation && couponCode 
                    ? `Free consultation (${couponCode.toUpperCase()})` 
                    : `Paid: ${currency} ${totalAmount.toFixed(2)}`}
                </Typography>

                <Divider sx={{ my: 2 }} />

                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  Your Information
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Name: {bookingConfirmation.bookerName}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Email: {bookingConfirmation.bookerEmail}
                </Typography>
                {bookingConfirmation.bookerPhone && (
                  <Typography variant="body2" color="text.secondary">
                    Phone No: {bookingConfirmation.bookerPhone}
                  </Typography>
                )}
              </Paper>
            )}
            {!finalizingBooking && (
              <Button
                variant="contained"
                onClick={() => {
                  setActiveStep(0);
                  setDate(new Date());
                  setSelectedSlot(null);
                  setBookerDetails({
                    name: '',
                    email: '',
                    phoneCountryCode: '+1',
                    phone: '',
                    message: '',
                  });
                  setAppointmentType('walk-in');
                  setWalkInArrivalMode('arriving_now');
                  setPaymentStatus('idle');
                  setPaymentMessage(null);
                  setBookingConfirmation(null);
                  // Clear coupon code when booking another appointment
                  setCouponCode('');
                  setCouponValid(null);
                }}
              >
                Book Another Appointment
              </Button>
            )}
          </Box>
        );
      default:
        return null;
    }
  };

  // Show loading state while fetching appointment taker
  if (loadingTaker) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: '#f3f4f6',
        }}
      >
        <CircularProgress size={40} />
      </Box>
    );
  }

  // Show error state if appointment taker not found
  if (takerError || !appointmentTaker) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          background: '#f3f4f6',
          px: 2,
        }}
      >
        <img
          src={cwLogo}
          alt="Canada Waleyar"
          style={{
            height: '80px',
            marginBottom: '24px',
          }}
        />
        <Paper
          elevation={0}
          sx={{
            p: 4,
            borderRadius: 2,
            border: '1px solid rgba(15, 15, 15, 0.08)',
            background: '#ffffff',
            textAlign: 'center',
            maxWidth: 400,
          }}
        >
          <Typography variant="h6" gutterBottom>
            Booking Unavailable
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
            {takerError || 'This booking link is invalid or expired.'}
          </Typography>
          <Button variant="contained" onClick={() => navigate('/')}>
            Return to Home
          </Button>
        </Paper>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        background: '#f3f4f6',
        px: 2,
      }}
    >
      <Box
        sx={{
          flex: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          pt: 4,
          pb: 2,
        }}
      >
        <Box sx={{ width: '100%', maxWidth: 520 }}>
          <Box sx={{ textAlign: 'center', mb: 4 }}>
            <img
              src={cwLogo}
              alt="Canada Waleyar"
              style={{
                height: '80px',
                marginBottom: '16px',
                padding: 0,
                borderRadius: 0,
              }}
            />
            <Typography variant="h4" component="h1" gutterBottom>
              Book Your Appointment
            </Typography>
            {appointmentTaker && (
              <Typography variant="body1" color="text.secondary">
                with {appointmentTaker.displayName.split(' ')[0]}
              </Typography>
            )}
          </Box>

          <Paper
            elevation={0}
            sx={{
              p: { xs: 2.5, sm: 4 },
              borderRadius: 2,
              border: '1px solid rgba(15, 15, 15, 0.08)',
              background: '#ffffff',
            }}
          >
          <Stepper activeStep={activeStep} alternativeLabel sx={{ mb: 3 }}>
            {steps.map((label, index) => (
              <Step 
                key={label}
                completed={index < activeStep || (index === 3 && activeStep === 3 && paymentStatus === 'success' && !finalizingBooking)}
              >
                <StepLabel
                  sx={{
                    '& .MuiStepLabel-iconContainer .Mui-completed': {
                      color: '#4caf50', // Green color for completed steps
                    },
                  }}
                >
                  {label}
                </StepLabel>
              </Step>
            ))}
          </Stepper>

          {error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}

          {renderStepContent(activeStep)}

          {activeStep < steps.length - 1 && activeStep !== 2 && (
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 4 }}>
              <Button
                variant="contained"
                onClick={handleNext}
                disabled={!canProceedFromStep(activeStep) || paymentStatus === 'processing'}
              >
                Next
              </Button>
            </Box>
          )}
        </Paper>
        </Box>
      </Box>

      <Box
        sx={{
          py: 2,
          textAlign: 'center',
          width: '100%',
        }}
      >
        <Typography
          variant="caption"
          sx={{
            color: 'text.secondary',
            fontSize: '0.75rem',
            fontWeight: 300,
          }}
        >
          © 2025 Canadawale Yar™. All rights reserved.
          <br />
          Powered by{' '}
          <Link
            href="https://stripe.com"
            target="_blank"
            rel="noopener noreferrer"
            sx={{
              color: 'text.secondary',
              textDecoration: 'none',
              '&:hover': {
                textDecoration: 'underline',
              }
            }}
          >
            Stripe
          </Link>
          {' | '}
          <Link
            href="https://canadawaleyar.com/privacy"
            target="_blank"
            rel="noopener noreferrer"
            sx={{
              color: 'text.secondary',
              textDecoration: 'none',
              '&:hover': {
                textDecoration: 'underline',
              }
            }}
          >
            Privacy Policy
          </Link>
          {' | '}
          <Link
            href="https://canadawaleyar.com/terms"
            target="_blank"
            rel="noopener noreferrer"
            sx={{
              color: 'text.secondary',
              textDecoration: 'none',
              '&:hover': {
                textDecoration: 'underline',
              }
            }}
          >
            Terms of Service
          </Link>
        </Typography>
      </Box>

    </Box>
  );
};

export default BookingPage;



